import { useEffect, useState } from 'react';
import { Alert, Snackbar, Stack, Typography } from '@mui/material';

import { useAdminPanelStore } from '@store/adminPanelStore';

import { snackbarTexts } from '../constants';
import { MenuOptionValue } from '../types';

interface FilesListProps {
    successedUploads: string[];
    failedUploads: string[];
}

const FilesList = ({ successedUploads, failedUploads }: FilesListProps) => (
    <Stack>
        <Typography>{`${snackbarTexts.uploadedLabel}: ${successedUploads.join(', ')}.`}</Typography>
        <Typography>{`${snackbarTexts.failedLabel}: ${failedUploads.join(', ')}.`}</Typography>
    </Stack>
);

interface SnackbarWithAlertProps {
    isError: boolean;
    isSuccess: boolean;
    error: Error | null;
    selectedMenuOption: MenuOptionValue;
}

const SnackbarWithAlert = ({
    isError,
    isSuccess,
    error,
    selectedMenuOption,
}: SnackbarWithAlertProps) => {
    const [isSnackbarOpen, setIsSnackbarOpen] = useState(isError || isSuccess);
    const filesUploadResponse = useAdminPanelStore((state) => state.filesUploadResponse);

    useEffect(() => {
        setIsSnackbarOpen(isError || isSuccess);
    }, [isError, isSuccess]);

    if (!isError && !isSuccess) return null;

    const handleSnackbarClose = () => {
        setIsSnackbarOpen(false);
    };

    const isRegionalKnowledgeFiles = selectedMenuOption === MenuOptionValue.RegionalKnowledgeFiles;
    const isFileUploadPartialSuccess = filesUploadResponse?.allSuccess === false;

    const isPartialSuccess = isRegionalKnowledgeFiles && isFileUploadPartialSuccess;

    const getMainMessage = () => {
        if (isSuccess) {
            return isPartialSuccess ? snackbarTexts.partialSuccess : snackbarTexts.success;
        }
        return `${snackbarTexts.error} ${error?.message}`;
    };

    return (
        <Snackbar
            open={isSnackbarOpen}
            autoHideDuration={isSuccess ? 3000 : null}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
            <Alert
                onClose={isSuccess ? undefined : handleSnackbarClose}
                severity={isSuccess ? (isPartialSuccess ? 'warning' : 'success') : 'error'}
                sx={{ width: '100%', alignItems: 'center' }}
            >
                <Stack direction='column' gap={1}>
                    <Typography>{getMainMessage()}</Typography>
                    {isPartialSuccess && (
                        <FilesList
                            successedUploads={filesUploadResponse.successedUploads}
                            failedUploads={filesUploadResponse.failedUploads}
                        />
                    )}
                </Stack>
            </Alert>
        </Snackbar>
    );
};

export default SnackbarWithAlert;
